import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PosCartDiscount, PosCartItem, PosCustomerSelection, PosRepairSelection } from '@/lib/pos/types.js';

type PosCartState = {
  storeId: string | null;
  items: PosCartItem[];
  customer: PosCustomerSelection | null;
  cartDiscount: PosCartDiscount | null;
  repairSelection: PosRepairSelection | null;
  loyaltyApplied: number;
  storeCreditApplied: number;
  /** Manager who authorized an over-limit discount for this cart */
  managerOverrideAuthorizedBy: string | null;
  setStoreId: (storeId: string) => void;
  addItem: (item: PosCartItem) => void;
  updateItem: (id: string, patch: Partial<PosCartItem>) => void;
  removeItem: (id: string) => void;
  setCustomer: (customer: PosCustomerSelection | null) => void;
  setCartDiscount: (discount: PosCartDiscount | null) => void;
  setRepairSelection: (selection: PosRepairSelection | null) => void;
  setLoyaltyApplied: (amount: number) => void;
  setStoreCreditApplied: (amount: number) => void;
  setManagerOverrideAuthorizedBy: (userId: string | null) => void;
  clearCart: () => void;
};

function newCartItemId(): string {
  return crypto.randomUUID();
}

export function createRepairCartItem(input: {
  serviceId: string;
  serviceName: string;
  price: number;
  makeId?: string;
  makeName?: string;
  modelId?: string;
  modelName?: string;
  estimatedMinutes?: number | null;
  warrantyDays?: number | null;
  warrantyType?: 'days' | 'lifetime' | 'none' | null;
}): PosCartItem {
  return {
    id: newCartItemId(),
    type: 'repair',
    quantity: 1,
    unitPrice: input.price,
    originalPrice: input.price,
    serviceId: input.serviceId,
    serviceName: input.serviceName,
    makeId: input.makeId,
    makeName: input.makeName,
    modelId: input.modelId,
    modelName: input.modelName,
    estimatedMinutes: input.estimatedMinutes,
    warrantyDays: input.warrantyDays,
    warrantyType: input.warrantyType,
  };
}

export function createInventoryCartItem(input: {
  inventoryItemId: string;
  name: string;
  price: number;
  sku?: string;
  qtyOnHand?: number;
  taxable?: boolean;
}): PosCartItem {
  return {
    id: newCartItemId(),
    type: 'inventory',
    quantity: 1,
    unitPrice: input.price,
    originalPrice: input.price,
    inventoryItemId: input.inventoryItemId,
    name: input.name,
    sku: input.sku,
    qtyOnHand: input.qtyOnHand,
    taxable: input.taxable ?? true,
  };
}

export function createCustomCartItem(description: string, price: number, quantity = 1): PosCartItem {
  return {
    id: newCartItemId(),
    type: 'custom',
    quantity,
    unitPrice: price,
    originalPrice: price,
    description,
    name: description,
    taxable: true,
  };
}

export function createFeeCartItem(input: {
  feePresetId: string;
  name: string;
  price: number;
}): PosCartItem {
  return {
    id: newCartItemId(),
    type: 'fee',
    quantity: 1,
    unitPrice: input.price,
    originalPrice: input.price,
    feePresetId: input.feePresetId,
    name: input.name,
    description: input.name,
    taxable: false,
  };
}

export function createTradeInCartItem(input: {
  deviceDescription: string;
  condition: string;
  offeredPrice: number;
}): PosCartItem {
  const price = -Math.abs(input.offeredPrice);
  return {
    id: newCartItemId(),
    type: 'trade_in',
    quantity: 1,
    unitPrice: price,
    originalPrice: price,
    deviceDescription: input.deviceDescription,
    condition: input.condition,
    name: `Trade-In: ${input.deviceDescription}`,
    description: `Trade-In: ${input.deviceDescription}`,
    taxable: false,
  };
}

export const usePosCartStore = create<PosCartState>()(
  persist(
    (set, get) => ({
      storeId: null,
      items: [],
      customer: null,
      cartDiscount: null,
      repairSelection: null,
      loyaltyApplied: 0,
      storeCreditApplied: 0,
      managerOverrideAuthorizedBy: null,
      setStoreId: (storeId) => {
        if (get().storeId && get().storeId !== storeId) {
          set({
            storeId,
            items: [],
            customer: null,
            cartDiscount: null,
            repairSelection: null,
            loyaltyApplied: 0,
            storeCreditApplied: 0,
            managerOverrideAuthorizedBy: null,
          });
          return;
        }
        set({ storeId });
      },
      addItem: (item) =>
        set((state) => {
          if (item.type === 'inventory' && item.inventoryItemId) {
            const existing = state.items.find(
              (row) => row.type === 'inventory' && row.inventoryItemId === item.inventoryItemId,
            );
            if (existing) {
              return {
                items: state.items.map((row) =>
                  row.id === existing.id ? { ...row, quantity: row.quantity + item.quantity } : row,
                ),
              };
            }
          }
          return { items: [...state.items, item] };
        }),
      updateItem: (id, patch) =>
        set((state) => ({
          items: state.items.map((row) => (row.id === id ? { ...row, ...patch } : row)),
        })),
      removeItem: (id) => set((state) => ({ items: state.items.filter((row) => row.id !== id) })),
      setCustomer: (customer) => set({ customer, loyaltyApplied: 0, storeCreditApplied: 0 }),
      setCartDiscount: (cartDiscount) =>
        set({
          cartDiscount,
          ...(cartDiscount ? {} : { managerOverrideAuthorizedBy: null }),
        }),
      setRepairSelection: (repairSelection) => set({ repairSelection }),
      setLoyaltyApplied: (loyaltyApplied) => set({ loyaltyApplied }),
      setStoreCreditApplied: (storeCreditApplied) => set({ storeCreditApplied }),
      setManagerOverrideAuthorizedBy: (managerOverrideAuthorizedBy) =>
        set({ managerOverrideAuthorizedBy }),
      clearCart: () =>
        set({
          items: [],
          cartDiscount: null,
          loyaltyApplied: 0,
          storeCreditApplied: 0,
          managerOverrideAuthorizedBy: null,
        }),
    }),
    {
      name: 'fixflow-pos-cart',
      partialize: (state) => ({
        storeId: state.storeId,
        items: state.items,
        customer: state.customer,
        cartDiscount: state.cartDiscount,
        repairSelection: state.repairSelection,
        loyaltyApplied: state.loyaltyApplied,
        storeCreditApplied: state.storeCreditApplied,
        managerOverrideAuthorizedBy: state.managerOverrideAuthorizedBy,
      }),
    },
  ),
);
